import { useCallback } from 'react'

import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'

import { Skeleton } from '@/components/ui/skeleton.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { $creatorId, $currentCreator } from '@/models/creators.ts'
import { RunBehaviorSelect } from '@/modules/automation-settings/components/run-behavior-select.tsx'
import {
  useAutomationSettings,
  useUpdateAutomationSettings,
} from '@/modules/automation-settings/query/automation-settings-query.ts'
import { ListAutomationSettingsResponseSchema } from '@/modules/automation-settings/service/automation-settings-service.ts'
import {
  automationItems,
  hasAutomationItemFeature,
} from '@/modules/creator-settings/data/automation-items.ts'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption.tsx'
import { handleError } from '@/utils/error'
import { debounce } from '@/utils/helpers.ts'

function formatUTC(date: Date) {
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')
  const seconds = String(date.getUTCSeconds()).padStart(2, '0')

  return `${hours}:${minutes}:${seconds}`
}

export const AutomationSettings = () => {
  const queryClient = useQueryClient()
  const creatorId = useUnit($creatorId)
  const { data, isFetching, isIdle } = useAutomationSettings()
  const updateAutomationSettings = useUpdateAutomationSettings()
  const toast = useToast()
  const activeCreator = useUnit($currentCreator)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleRunBehaviorChange = useCallback(
    debounce(
      (
        serviceType: string,
        runBehavior: string,
        fromTimeDate?: Date,
        toTimeDate?: Date,
      ) => {
        const fromTime = fromTimeDate && formatUTC(fromTimeDate)
        const toTime = toTimeDate && formatUTC(toTimeDate)

        updateAutomationSettings.mutate(
          { serviceType, runBehavior, fromTime, toTime },
          {
            onSuccess: (response) => {
              const parsedData = ListAutomationSettingsResponseSchema.safeParse(
                response.data,
              )

              if (!parsedData.success) {
                toast.error('Error while updating automation settings')

                return
              }

              queryClient.setQueryData(
                ['automation-settings', creatorId],
                parsedData.data,
              )
              toast.success('Automation settings updated')
            },
            onError: (error) => {
              handleError(error, 'Error while updating automation settings')
            },
          },
        )
      },
      800,
    ),
    [data, queryClient, toast, updateAutomationSettings],
  )

  const getAutomationServiceByType = useCallback(
    (serviceType: string) => {
      return data?.find((item) => item.serviceType === serviceType)
    },
    [data],
  )

  return (
    <>
      {(isFetching || isIdle || !activeCreator) && (
        <Skeleton className="mt-8 h-80 bg-skeleton" />
      )}
      {!isFetching && !isIdle && activeCreator && (
        <div className="mt-8 grid divide-y divide-border">
          {automationItems
            .filter((item) => hasAutomationItemFeature(activeCreator, item))
            .map((item, index) => {
              const service = getAutomationServiceByType(item.type)
              const title =
                typeof item.title === 'function'
                  ? item.title(activeCreator)
                  : item.title

              return (
                <SettingsOption
                  key={item.type}
                  title={title}
                  description=""
                  disableTopPadding={index === 0}
                  mobileVertical
                >
                  <RunBehaviorSelect
                    item={item}
                    itemTitle={title}
                    service={service}
                    handleRunBehaviorChange={handleRunBehaviorChange}
                  />
                </SettingsOption>
              )
            })}
        </div>
      )}
    </>
  )
}
